import { css, StyleSheet } from 'aphrodite';
import { useEffect, useMemo, useState } from 'react';
import { grayIconFill } from '../../../styles/colors';
import { THEME } from '../../Theme/theme';

const DEFAULT_MESSAGE = 'Levitating';

/** Fun phrases to rotate through while the AI assistant is thinking. */
export const AI_THINKING_MESSAGES = [
	'Levitating',
	'Working a little magic',
	'Setting the stage',
	'Shuffling things into place',
	'Drumroll, please',
	'Defying gravity',
];

/** Interval in seconds between message rotations */
const MESSAGE_ROTATION_INTERVAL = 4;

interface ThinkingIndicatorProps {
	/** Custom messages to rotate through. If provided, will cycle through these messages. */
	messages?: string[];
	/** Whether to show elapsed time. Defaults to true. */
	showTime?: boolean;
}

/**
 * A thinking indicator that displays elapsed time with animated dots.
 * Used in AI Assistant chat to show that a response is being generated.
 *
 * @example
 * ```tsx
 * // Default usage
 * <ThinkingIndicator />
 * // Renders: "Levitating (2s)..."
 *
 * // With custom rotating messages
 * <ThinkingIndicator messages={['Searching...', 'Analyzing...', 'Almost there...']} />
 * // Rotates through messages every 4 seconds
 * ```
 */
export function ThinkingIndicator({ messages, showTime = true }: ThinkingIndicatorProps) {
	const [elapsedSeconds, setElapsedSeconds] = useState(0);

	useEffect(() => {
		const interval = setInterval(() => {
			setElapsedSeconds(s => s + 1);
		}, 1000);
		return () => clearInterval(interval);
	}, []);

	// Calculate which message to show based on elapsed time
	const currentMessage = useMemo(() => {
		if (!messages || messages.length === 0) {
			return DEFAULT_MESSAGE;
		}
		const messageIndex = Math.floor(elapsedSeconds / MESSAGE_ROTATION_INTERVAL) % messages.length;
		return messages[messageIndex];
	}, [messages, elapsedSeconds]);

	return (
		<span className={css(styles.container)}>
			{currentMessage}
			{showTime && ` (${elapsedSeconds}s)`}
			<span className={css(styles.dots)}>
				<span className={css(styles.dot, styles.dot1)}>.</span>
				<span className={css(styles.dot, styles.dot2)}>.</span>
				<span className={css(styles.dot, styles.dot3)}>.</span>
			</span>
		</span>
	);
}

const dotAnimation = {
	'0%, 20%': {
		opacity: 0,
	},
	'40%': {
		opacity: 1,
	},
	'100%': {
		opacity: 1,
	},
};

const styles = StyleSheet.create({
	container: {
		color: grayIconFill,
		fontSize: THEME.typography.fontSizes.sm,
		fontWeight: THEME.typography.fontWeights.standard,
	},
	dots: {
		display: 'inline-block',
		width: 16,
		marginLeft: 2,
	},
	dot: {
		animationName: [dotAnimation],
		animationDuration: '1.4s',
		animationIterationCount: 'infinite',
		animationFillMode: 'both',
	},
	dot1: {
		animationDelay: '0s',
	},
	dot2: {
		animationDelay: '0.2s',
	},
	dot3: {
		animationDelay: '0.4s',
	},
});
